/**
 * PostaList — Phase 8.1 list workspace.
 *
 * Composes the calibrated workspace pattern:
 * Shell · AppRail · Crown (breadcrumb + display + CrownMeta + Subtabs
 * Odeslaná / Přijatá / Všechny) · Toolband (search + datum-od / datum-do +
 * Nová zásilka) · DataTable · Inspector preview.
 *
 * URL search-params (`?tab=&search=&od=&do=`) drive filters via TanStack
 * Router `validateSearch`; the detail route reads the same params so the
 * narrow rail mirrors what the user was browsing.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Inspector, FieldRow } from '@/components/Inspector';
import { LoadMoreBar } from '@/components/LoadMoreBar';
import { Marker } from '@/components/Marker';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { PostaSubtabs } from './PostaSubtabs';
import { Toolband } from '@/components/Toolband';
import { formatDate, formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useZasilkyInfiniteQuery, type ZasilkaWithCarrier } from './api';
import {
  matchesSearch,
  typFromTab,
  typLabel,
  typTone,
  type PostaSearch,
  type PostaTab,
} from './helpers';

const COLUMNS_TEMPLATE =
  '110px 100px minmax(0,1fr) minmax(0,1.4fr) minmax(0,1fr) 130px';

export function PostaList() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/posta' });
  const tab: PostaTab = urlSearch.tab ?? 'odeslana';
  const od = urlSearch.od ?? '';
  const doDate = urlSearch.do ?? '';

  const { isPhone } = useBreakpoint();
  const { can } = useModuleAccess();
  const canWrite = can('posta', 'write');
  const [search, setSearch] = useState(urlSearch.search ?? '');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const setTab = (next: PostaTab) => {
    navigate({
      to: '/posta',
      search: {
        ...urlSearch,
        tab: next === 'odeslana' ? undefined : next,
      },
      replace: true,
    });
  };

  const setDateFilter = (key: 'od' | 'do', value: string) => {
    navigate({
      to: '/posta',
      search: {
        ...urlSearch,
        [key]: value || undefined,
      },
      replace: true,
    });
  };

  const filters = useMemo(() => {
    const typ = typFromTab(tab);
    return {
      ...(typ ? { typ_zasilky: typ } : {}),
      ...(od ? { datum_od: od } : {}),
      ...(doDate ? { datum_do: doDate } : {}),
    };
  }, [tab, od, doDate]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useZasilkyInfiniteQuery(filters);
  const zasilky = useMemo(
    () => data?.pages.flatMap((p) => p.zasilky) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  const visible = useMemo(() => {
    if (!debouncedSearch) return zasilky;
    return zasilky.filter((z) => matchesSearch(z, debouncedSearch));
  }, [zasilky, debouncedSearch]);

  const selected =
    visible.find((z) => z.id_zasilky === selectedId) ?? visible[0] ?? null;

  const counts = useMemo(() => {
    const acc = { total, prijate: 0, odeslane: 0 };
    for (const z of zasilky) {
      if (z.typ_zasilky === 1) acc.prijate += 1;
      else acc.odeslane += 1;
    }
    return acc;
  }, [zasilky, total]);

  return (
    <Shell
      rail={<AppRail active="posta" />}
      inspector={<PostaInspector zasilka={selected} urlSearch={urlSearch} />}
    >
      <Crown>
        <Breadcrumb segments={[{ label: 'Pošta' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>Pošta</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.total) },
              { label: 'Odeslané', value: formatInt(counts.odeslane) },
              { label: 'Přijaté', value: formatInt(counts.prijate) },
            ]}
          />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <PostaSubtabs active={tab} count={visible.length} onListChange={setTab} />
        </Crown.Row>
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="Popis, adresát, společnost, podací číslo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          wrapperClassName="max-w-[360px] flex-1"
        />
        <DateInput
          label="Od"
          value={od}
          onChange={(v) => setDateFilter('od', v)}
        />
        <DateInput
          label="Do"
          value={doDate}
          onChange={(v) => setDateFilter('do', v)}
        />
        {(od || doDate || search) && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              navigate({
                to: '/posta',
                search: tab === 'odeslana' ? {} : { tab },
                replace: true,
              });
            }}
            className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink"
          >
            Vymazat filtr
          </button>
        )}
        <Toolband.Spacer />
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/posta/novy' })}
          >
            Nová zásilka
          </Button>
        )}
      </Toolband>

      <div
        data-testid="posta-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1"
      >
        <DataTable<ZasilkaWithCarrier>
          aria-label="Seznam zásilek"
          variant="bordered"
          columns={zasilkaColumns()}
          data={isLoading ? [] : visible}
          getRowId={(z) => String(z.id_zasilky)}
          selectedId={selected ? String(selected.id_zasilky) : undefined}
          onRowClick={(z) =>
            // Phone (14.3): no inspector exists — tapping a card navigates to
            // the detail route, forwarding the list cohort for its BackBar.
            isPhone
              ? navigate({
                  to: '/posta/$id',
                  params: { id: String(z.id_zasilky) },
                  search: urlSearch,
                })
              : setSelectedId(z.id_zasilky)
          }
          empty={
            isLoading ? (
              <ListSkeleton />
            ) : isError ? (
              <EmptyState
                eyebrow="Chyba"
                title="Zásilky se nepodařilo načíst"
                description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
              />
            ) : (
              <EmptyState
                eyebrow="Prázdné"
                title="Žádné zásilky"
                description="Zadanému filtru nevyhovuje žádná zásilka."
              />
            )
          }
          className="min-h-0 flex-1"
        />
      </div>
      <LoadMoreBar
        loaded={zasilky.length}
        total={total}
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        unit="zásilek"
      />
    </Shell>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 border border-line bg-paper px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
      <span>{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-[12px] font-medium normal-case tracking-normal text-ink outline-none"
      />
    </label>
  );
}

function zasilkaColumns(): DataTableColumn<ZasilkaWithCarrier>[] {
  const widths = COLUMNS_TEMPLATE.split(' ');
  return [
    {
      id: 'datum',
      priority: 'meta',
      header: 'Datum',
      width: widths[0],
      cell: (z) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {formatDate(z.datum_zasilky) || '—'}
        </span>
      ),
    },
    {
      id: 'typ',
      priority: 'meta',
      header: 'Typ',
      width: widths[1],
      cell: (z) => <Marker tone={typTone(z.typ_zasilky)}>{typLabel(z.typ_zasilky)}</Marker>,
    },
    {
      id: 'spolecnost',
      priority: 'secondary',
      header: 'Společnost',
      width: widths[2],
      cell: (z) => (
        <span className="truncate text-[12.5px] text-ink-2">{z.nazev || '—'}</span>
      ),
    },
    {
      id: 'popis',
      priority: 'primary',
      header: 'Popis zásilky',
      width: widths[3],
      cell: (z) => (
        <span className="truncate font-medium tracking-[-0.01em] text-ink">
          {z.popis_zasilky || '—'}
        </span>
      ),
    },
    {
      id: 'adresat',
      priority: 'secondary',
      header: 'Adresát',
      width: widths[4],
      cell: (z) => (
        <span className="truncate text-[12px] text-ink-2">{z.adresat || '—'}</span>
      ),
    },
    {
      id: 'podaci',
      priority: 'meta',
      header: 'Podací č.',
      width: widths[5],
      cell: (z) => (
        <span
          className={
            'font-mono text-[11px] tracking-[-0.02em] ' +
            (z.podaci_cislo ? 'text-ink-2' : 'text-ink-3')
          }
        >
          {z.podaci_cislo || '—'}
        </span>
      ),
    },
  ];
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function PostaInspector({
  zasilka,
  urlSearch,
}: {
  zasilka: ZasilkaWithCarrier | null;
  urlSearch: PostaSearch;
}) {
  const navigate = useNavigate();
  if (!zasilka) {
    return (
      <Inspector>
        <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Vyberte zásilku
        </div>
      </Inspector>
    );
  }

  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>№ {zasilka.id_zasilky}</b> · {typLabel(zasilka.typ_zasilky)}
          </>
        }
        title={zasilka.popis_zasilky || 'Zásilka'}
        sub={
          zasilka.nazev ? (
            <span className="font-sans text-[12.5px] text-ink-2">{zasilka.nazev}</span>
          ) : null
        }
      />
      <Inspector.Body>
        <Inspector.Section heading="— Detail">
          <FieldRow k="Datum" v={formatDate(zasilka.datum_zasilky) || '—'} mono />
          <FieldRow k="Adresát" v={zasilka.adresat || '—'} />
          <FieldRow k="Podací č." v={zasilka.podaci_cislo || '—'} mono />
        </Inspector.Section>
        {zasilka.adresa && (
          <Inspector.Section heading="— Adresa">
            <FieldRow k="Adresa" v={zasilka.adresa} />
          </Inspector.Section>
        )}
        {zasilka.poznamka && (
          <Inspector.Section heading="— Poznámka">
            <FieldRow k="Text" v={zasilka.poznamka} />
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/posta/$id',
              params: { id: String(zasilka.id_zasilky) },
              search: urlSearch,
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}
